import React, { FC, useMemo, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import createHotAlertConfig from '../../../../api/hotAlert/createHotAlertConfig';
import updateHotAlertConfig from '../../../../api/hotAlert/updateHotAlertConfig';
import createSurveyConfig from '../../../../api/survey/createSurveyConfig';
import setupSurveyContent from '../../../../api/survey/setupSurveyContent';
import setupSurveyContentSqlServer from '../../../../api/survey/setupSurveyContentSqlServer';
import syncSurveyDatas from '../../../../api/survey/syncSurveyDatas';
import updateSurvey from '../../../../api/survey/updateSurvey';
import updateSurveyContent from '../../../../api/survey/updateSurveyContent';
import { TenantContext } from '../../../../contexts/tenantContext';
import { PipelineContext, PipelineStepConfig } from '../../../../utils/hooks/usePipeline';
import DeploymentPipeline from '../../../DeploymentPipeline/DeploymentPipeline';
import { SurveyStatus } from '../../../Surveys/surveyInterface';
import { ConfiguratedHotAlerts, PersonalizedSurveyProps, SelectedQuestion } from '../addSurveyInterface';

export interface EditedSurvey {
    surveyId: number;
    hotAlertConfigId?: number;
}

interface StepDeploymentProps {
    personalizedSurvey: PersonalizedSurveyProps;
    configuratedHotAlerts?: ConfiguratedHotAlerts;
    selectedQuestions: SelectedQuestion[];
    selectedFilters: SelectedQuestion[];
    onSuccess: () => void;
    // Set when an existing survey is edited: the survey is updated instead of created
    editedSurvey?: EditedSurvey;
}

// Questions removed during the edition may still be referenced by the hot alert configuration
const keepSelectedQuestionsOnly = (config: ConfiguratedHotAlerts, selectedQuestions: SelectedQuestion[]): ConfiguratedHotAlerts => {
    const selectedIds = new Set(selectedQuestions.map((sq) => sq.question.id));
    const keepIfSelected = (id?: number) => (id !== undefined && selectedIds.has(id) ? id : undefined);

    return {
        ...config,
        openedQuestion: keepIfSelected(config.openedQuestion),
        firstName: keepIfSelected(config.firstName),
        lastName: keepIfSelected(config.lastName),
        phone: keepIfSelected(config.phone),
        mail: keepIfSelected(config.mail),
        satisfaction: config.satisfaction && selectedIds.has(config.satisfaction.questionId) ? config.satisfaction : undefined,
        additionalQuestion: config.additionalQuestion.filter((id) => selectedIds.has(id)),
        detailQuestions: config.detailQuestions.filter((id) => selectedIds.has(id)),
    };
};

const StepDeployment: FC<StepDeploymentProps> = ({
    personalizedSurvey,
    configuratedHotAlerts,
    selectedQuestions,
    selectedFilters,
    onSuccess,
    editedSurvey,
}) => {
    const { t } = useTranslation('settings');
    const [selectedSchema] = useContext(TenantContext) ?? [];
    const createdSurveyIdRef = useRef<number | null>(null);

    const creationSteps: PipelineStepConfig[] = useMemo(() => {
        const createSurveyStep: PipelineStepConfig = {
            id: 'create-survey',
            label: t('tabs.createSurvey.step6Content.step1Label'),
            apiCall: async () => {
                const result = await createSurveyConfig(personalizedSurvey, selectedSchema || '');
                createdSurveyIdRef.current = result.surveyid;

                return result;
            },
        };

        const setupSurveyContentStep: PipelineStepConfig = {
            id: 'setup-survey-content',
            label: t('tabs.createSurvey.step6Content.setupContentLabel'),
            apiCall: ({ results }: PipelineContext) => {
                const surveyId = (results['create-survey'] as { surveyid: number }).surveyid;

                return setupSurveyContent(surveyId, personalizedSurvey.schema, selectedQuestions, selectedFilters, selectedSchema || '');
            },
        };

        const createHotAlertStep: PipelineStepConfig = {
            id: 'create-hot-alert',
            label: t('tabs.createSurvey.step6Content.step2Label'),
            apiCall: ({ results }: PipelineContext) => {
                const surveyId = (results['create-survey'] as { surveyid: number }).surveyid;

                return createHotAlertConfig(surveyId, configuratedHotAlerts!, selectedSchema || '');
            },
        };

        const createSqlServerTablesStep: PipelineStepConfig = {
            id: 'create-sql-server-tables',
            label: t('tabs.createSurvey.step6Content.step3Label'),
            apiCall: ({ results }: PipelineContext) => {
                const surveyId = (results['create-survey'] as { surveyid: number }).surveyid;

                return setupSurveyContentSqlServer(surveyId, personalizedSurvey.hasHotAlert, true, selectedSchema || '');
            },
        };

        const baseSteps = [createSurveyStep, setupSurveyContentStep];
        if (personalizedSurvey.hasHotAlert) { baseSteps.push(createHotAlertStep); }
        baseSteps.push(createSqlServerTablesStep);

        return baseSteps;
    }, [personalizedSurvey, configuratedHotAlerts, selectedQuestions, selectedFilters, t, selectedSchema]);

    const updateSteps: PipelineStepConfig[] = useMemo(() => {
        if (!editedSurvey) { return []; }
        const { surveyId, hotAlertConfigId } = editedSurvey;

        const updateSurveyStep: PipelineStepConfig = {
            id: 'update-survey',
            label: t('tabs.createSurvey.step6Content.updateSurveyLabel'),
            apiCall: () => updateSurvey(surveyId, personalizedSurvey, selectedSchema || ''),
        };

        const updateSurveyContentStep: PipelineStepConfig = {
            id: 'update-survey-content',
            label: t('tabs.createSurvey.step6Content.updateContentLabel'),
            apiCall: () => updateSurveyContent(surveyId, selectedQuestions, selectedFilters, selectedSchema || ''),
        };

        const updateHotAlertStep: PipelineStepConfig = {
            id: 'update-hot-alert',
            label: t('tabs.createSurvey.step6Content.updateHotAlertLabel'),
            apiCall: () => {
                const hotAlertConfig = keepSelectedQuestionsOnly(configuratedHotAlerts!, selectedQuestions);

                return hotAlertConfigId !== undefined
                    ? updateHotAlertConfig(surveyId, hotAlertConfigId, hotAlertConfig, selectedSchema || '')
                    : createHotAlertConfig(surveyId, hotAlertConfig, selectedSchema || '');
            },
        };

        const baseSteps = [updateSurveyStep, updateSurveyContentStep];
        if (personalizedSurvey.hasHotAlert) { baseSteps.push(updateHotAlertStep); }

        return baseSteps;
    }, [editedSurvey, personalizedSurvey, configuratedHotAlerts, selectedQuestions, selectedFilters, t, selectedSchema]);

    const handleSync = async (): Promise<void> => {
        const surveyId = createdSurveyIdRef.current;
        if (surveyId === null) { return; }

        await syncSurveyDatas(surveyId, true, selectedSchema || '', t('tabs.createSurvey.step6Content.syncSuccess'), t('tabs.createSurvey.step6Content.syncError'));
        await updateSurvey(surveyId, { ...personalizedSurvey, status: SurveyStatus.COMPLETED }, selectedSchema || '');
    };

    if (editedSurvey) {
        // No sync button here: the reload action of the surveys list lets the user choose
        // between an incremental and a full synchronisation.
        return (
            <DeploymentPipeline
                launchLabel={t('tabs.createSurvey.step6Content.launchUpdate')}
                onSuccess={onSuccess}
                steps={updateSteps}
                successLabel={t('tabs.createSurvey.step6Content.updateSuccess')}
            />
        );
    }

    return (
        <DeploymentPipeline
            onSuccess={onSuccess}
            onSync={handleSync}
            steps={creationSteps}
        />
    );
};

export default StepDeployment;
